import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import {computed} from "vue";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import dayjs from "@/utils/dayjs";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import type {ICellRendererParams} from "ag-grid-community";
import FieldMetaData = namaTypes.FieldMetaData;
import ESignatureInfo = namaTypes.ESignatureInfo;

export default class FileUploaderWidgetUtil extends FieldWidgetUtil {
  eSignature: boolean = false;
  scanByDefault: boolean = false;

  constructor(presenter: WidgetsPresenter, fieldMetaData: FieldMetaData, fieldId: string,agCellParams?: ICellRendererParams, field?: any) {
    super(presenter, fieldMetaData);
    const isApprovalContext = field?.isApprovalContext ?? false;
    if (ClientCache.isAttachmentSignature(presenter.entityType, fieldId, isApprovalContext))
      this.eSignature = true;
    else if (ClientCache.isAttachmentUsingScannerByDefault(presenter.entityType, fieldId))
      this.scanByDefault = true;
    if (agCellParams) {
      this.agCellParams = agCellParams;
      if (agCellParams.value)
        this.inputFieldValueRef.value = agCellParams.value
    }
  }

  override onPartialUserInput = async (info: { files: readonly any[]; xhr: any; }) => {
    if (info && info.files) {
      const file = info.files[0];
      const xhr = info.xhr;
      if (xhr) {
        this.inputFieldValueRef.value.attachmentInfo = {
          fileId: JSON.parse(xhr.response).fileId,
          fileName: file.name,
          updated: true,
          removed: false
        };
        this.inputFieldValueRef.value.dataLength = file.size;
        await this.onUserInputFinished();
      } else {
        console.error('XHR object is not available for file:', file.name);
      }
    }
  }

  override onUserInputFinished = async () => {
    let parsedValue = this.formatterInstance.parse(this.fieldMetaData.fieldSpecs, this.inputFieldValueRef.value);
    let originalValue = this.fetchFieldValue();
    await this.updateValueAndCallPostAction(parsedValue, originalValue);
    this.fitAgColumn()
  }

  async removeFile(forceEnableField: boolean | undefined) {
    if (this.isFieldDisabled(forceEnableField))
      return;
    if (this.inputFieldValueRef.value.attachmentInfo)
      this.inputFieldValueRef.value.attachmentInfo.removed = true;
    let parsedValue = this.formatterInstance.parse(this.fieldMetaData.fieldSpecs, this.inputFieldValueRef.value);
    let originalValue = this.fetchFieldValue();
    await this.updateValueAndCallPostAction(parsedValue, originalValue);
    this.fitAgColumn();
  }

  private fitAgColumn() {
    if (!this.agCellParams)
      return;
    const api = this.agCellParams.api;
    const rowNode = api.getRowNode(this.agCellParams.data.id);
    if (!rowNode)
      return;
    const colId = this.agCellParams.column?.getColId()!;
    api.autoSizeColumns([colId]);
  }

  onRejected(rejectedEntries: { failedPropValidation: string, file: File }[]) {
    rejectedEntries.forEach((rejectedEntry) => {
      console.error(rejectedEntry.failedPropValidation);
    });
  }

  isAttachmentExist(): boolean {
    if (this.inputFieldValueRef.value && this.inputFieldValueRef.value.attachmentInfo && this.inputFieldValueRef.value.attachmentInfo.fileId)
      return !this.inputFieldValueRef.value.attachmentInfo.removed;
    return false;
  }

  computedUrl() {
    return computed(() => {
      if (!this.isAttachmentExist())
        return "";
      return `file.download?fileID=${this.inputFieldValueRef.value.attachmentInfo.fileId}&entitytype=${this.presenter.entityType}&ischanged=${this.isChanged()}`;
    });
  }

  private isChanged(): boolean {
    if (!this.inputFieldValueRef.value || !this.inputFieldValueRef.value.attachmentInfo)
      return false;
    return this.inputFieldValueRef.value.attachmentInfo.updated ?? false;
  }

  getFileName(): string {
    if (!this.isAttachmentExist())
      return "";
    return this.inputFieldValueRef.value.attachmentInfo.fileName;
  }

  onDataReadyFromScannerOrSignature = async (dataURL: any, fromSignature?: boolean) => {
    let fileId: string | undefined = "";
    if (this.inputFieldValueRef.value && this.inputFieldValueRef.value.attachmentInfo)
      fileId = this.inputFieldValueRef.value.attachmentInfo.fileId;
    if (!fileId)
      fileId = await ClientCache.asyncNextGUIDId();
    let fileName = (fromSignature ? "signature-" : "scan-") + dayjs(Date.now()).format("YYYY-MM-DD-HH.mm") + (fromSignature ? ".png" : ".pdf");
    let settings: NamaAxiosRequestConfig = {
      params: {
        fileName: fileName,
        fileId: fileId
      },
      data: NamaCommon.stringify(dataURL)
    }
    let response = await NamaCommon.namaFetch(NamaURLS.UploadFileByDataURL, settings);
    if (response && response.result && response.result.succeded) {
      this.inputFieldValueRef.value.attachmentInfo = {
        fileId: fileId,
        fileName: fileName,
        updated: true,
        removed: false
      };
      await this.onUserInputFinished();
    }
  }

  static getProfileNumber(): number {
    let defaultCScanTwainProfileNo: string | undefined = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.defaultCScanTwainProfileNo;
    if (!defaultCScanTwainProfileNo)
      return 4;
    if (defaultCScanTwainProfileNo === "BlackAndWhiteDocument")
      return 3;
    else if (defaultCScanTwainProfileNo === "ColorDocument")
      return 4;
    else if (defaultCScanTwainProfileNo === "Grayscale")
      return 7;
    else if (defaultCScanTwainProfileNo === "ShowScannerUI")
      return 8;
    return 4;
  }

  static getUrl(files: any) {
    if (!files || !files[0])
      return "";
    return `upload`;
  }

  static async startScan(fileUploader: FileUploaderWidgetUtil) {
    const wn = window as any;
    let scannerApp: string | undefined = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.defaultScannerApp;
    let profileNumber: Number = FileUploaderWidgetUtil.getProfileNumber();
    let page: string = "cScanTwain/index.html?";
    let h: number = wn.screen.height;
    let w: Number = wn.screen.width;
    if (scannerApp === "DynamicWebTWAIN")
      page = "dynamicWebTWAIN/index.html?";
    let params: string = "profile=" + profileNumber + "&";
    wn.onScannerOK = async () => {
      await fileUploader.onDataReadyFromScannerOrSignature(wn.dataFromScanner)
    }
    wn.open(page + params, "scanner", "height=" + h + ",width=" + w);
  }

  static async onSignatureClick(fileUploader: FileUploaderWidgetUtil, forceEnableField: boolean | undefined) {
    if (fileUploader.isFieldDisabled(forceEnableField))
      return;
    const wn = window as any;
    let info: ESignatureInfo = ClientCache.fetchSignatureLineApplied(fileUploader.presenter.entityType, fileUploader.fieldId());
    let height: number = info.height || 500;
    let width: number = info.width || 500;
    let params: string = "";
    if (info.lineWidth)
      params = "lineWidth=" + info.lineWidth + "&";
    if (info.imageUrl)
      params = params + "imgId=" + info.imageUrl;
    let page: string = "e-sig/index.html?";
    let windowHeight: number = height;
    let windowWidth: number = width;
    if (info.epadInk) {
      page = "e-sig/epadink.html?";
      windowHeight += 200;
      windowWidth += 200;
    }
    wn.onESignatureOK = async () => {
      await fileUploader.onDataReadyFromScannerOrSignature(wn.eSignataureDataURL, true)
    }
    wn.open(page + params, "esignature", "height=" + windowHeight + ",width=" + windowWidth);
  }
}
